"use client";
import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";

import TippyTooltip, { ToolTipProps } from "@stadium/ui/src/components/tooltip/tooltip";

import StyledButton from "@stadium/ui/src/components/button/styled-button";

import Multimedia from "@stadium/ui/src/components/multimedia/multimedia";

import style from "./main.module.scss";

interface ImageProps {
  leftImageUrl?: string;
  leftImageWidth?: number;
  leftImageHeight?: number;
  isLeftNextImage?: boolean;
}
interface ControlledTooltipProps
  extends Pick<ToolTipProps, "placement" | "heading" | "interactive">,
    ImageProps {
  bodyContent: ReactNode;
  isDefaultTooltipVisible?: boolean;
  openOnHover?: boolean;
  isCrossIconRequired?: boolean;
  footerCloseButtonCtaText?: string;
  footerCloseButtonAction?: () => void;
  actionBlockClass?: string;
  actionButtonClass?: string;
  containerClassName?: string;
  imageBlockClass?: string;
  tipContainerClass?: string;
  tippyBodyContentClass?: string;
  actionButtonStyleType?: React.ComponentProps<typeof StyledButton>["styleType"];
  headingClass?: string;
  getTippyInstanceStatus?: (Boolean) => void;
  children?: ReactNode;
  renderFooter?: (opts: { cta?: React.ReactNode; className: string }) => React.ReactNode;
}

const ControlledTooltip: FC<ControlledTooltipProps> = ({
  heading = "",
  bodyContent = "",
  footerCloseButtonCtaText = "",
  footerCloseButtonAction,
  actionBlockClass = "",
  actionButtonClass = "",
  actionButtonStyleType = "no-style",
  isDefaultTooltipVisible = false,
  isCrossIconRequired = true,
  placement = "top",
  interactive = false,
  tipContainerClass = "",
  tippyBodyContentClass = "",
  imageBlockClass = "",
  openOnHover = false,
  leftImageUrl = "",
  isLeftNextImage = false,
  headingClass = "",
  leftImageWidth = 80,
  leftImageHeight = 80,
  children,
  containerClassName = "",
  getTippyInstanceStatus,
  renderFooter,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(isDefaultTooltipVisible);
  const showTooltip = () => setIsTooltipVisible(true);
  const hideTooltip = () => setIsTooltipVisible(false);

  const footerCloseButtonHandler = () => {
    footerCloseButtonAction?.();
    hideTooltip();
  };

  useEffect(() => {
    if (getTippyInstanceStatus) getTippyInstanceStatus(isTooltipVisible);
  }, [isTooltipVisible]);

  const childComponent = useMemo(
    () =>
      React.isValidElement(children) &&
      React.cloneElement(children, { onClick: isTooltipVisible ? hideTooltip : showTooltip }),
    [children, isTooltipVisible],
  );

  const renderTippyFooter = () => {
    const wrapperClass = `${style.actionBlock} ${actionBlockClass}`;
    const cta = footerCloseButtonCtaText ? (
      <StyledButton
        className={actionButtonClass}
        onClick={footerCloseButtonHandler}
        styleType={actionButtonStyleType}
        styles={style}
      >
        {footerCloseButtonCtaText}
      </StyledButton>
    ) : null;
    if (renderFooter) {
      return renderFooter({
        cta,
        className: wrapperClass,
      });
    }
    return cta && <div className={wrapperClass}>{cta}</div>;
  };

  const renderTippyContent = () => {
    return (
      <div className={tipContainerClass}>
        {isCrossIconRequired && (
          <button className={style.closeIconBlock} onClick={hideTooltip}>
            <span className={style.iconClose} />
          </button>
        )}
        <div className={style.tipContent}>
          <div className={`${style.tippyBodyContent} ${tippyBodyContentClass}`}>
            {leftImageUrl && (
              <div className={`${style.leftImageBlock} ${imageBlockClass}`}>
                <Multimedia
                  type="image"
                  src={leftImageUrl}
                  width={leftImageWidth}
                  height={leftImageHeight}
                  alt="image"
                  className={style.leftImage}
                  isNextImage={isLeftNextImage}
                />
              </div>
            )}
            <div className={style.rightContentBlock}>
              {heading && <div className={`${style.tipHeading} ${headingClass}`}>{heading}</div>}
              {bodyContent}
            </div>
          </div>
        </div>
        {renderTippyFooter()}
      </div>
    );
  };

  const visibleProp = !openOnHover ? { visible: isTooltipVisible } : {};

  return (
    <TippyTooltip
      placement={placement}
      content={renderTippyContent()}
      arrow
      isLightTheme
      interactive={interactive}
      {...visibleProp}
      onClickOutside={hideTooltip}
      containerClass={`${style.tippyContainerBlock} ${containerClassName}`}
    >
      {children && childComponent}
    </TippyTooltip>
  );
};
export default ControlledTooltip;
